import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

const BLACK  = rgb(0,    0,    0);
const GRAY   = rgb(0.45, 0.45, 0.45);
const LGRAY  = rgb(0.82, 0.82, 0.82);
const FILL   = rgb(0.93, 0.93, 0.93);

const fmt2 = (n) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toWords = (n) => {
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
    'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const convert = (num) => {
    if (num === 0) return '';
    if (num < 20)       return ones[num];
    if (num < 100)      return tens[Math.floor(num/10)] + (num%10 ? ' '+ones[num%10] : '');
    if (num < 1000)     return ones[Math.floor(num/100)] + ' Hundred' + (num%100 ? ' '+convert(num%100) : '');
    if (num < 100000)   return convert(Math.floor(num/1000)) + ' Thousand' + (num%1000 ? ' '+convert(num%1000) : '');
    if (num < 10000000) return convert(Math.floor(num/100000)) + ' Lakh' + (num%100000 ? ' '+convert(num%100000) : '');
    return convert(Math.floor(num/10000000)) + ' Crore' + (num%10000000 ? ' '+convert(num%10000000) : '');
  };
  const rupees = Math.floor(n);
  const paise  = Math.round((n - rupees) * 100);
  let w = convert(rupees) || 'Zero';
  w += ' Rupees';
  if (paise > 0) w += ' and ' + convert(paise) + ' Paise';
  return w + ' Only';
};

export async function generateSalesOrderPDF(order) {
  const pdfDoc = await PDFDocument.create();
  const font   = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontB  = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const fontO  = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  const PAGE_W = 595;
  const ML = 28, MR = 28;
  const CW = PAGE_W - ML - MR;
  const RIGHT = PAGE_W - MR;

  const items = Array.isArray(order.items) ? order.items.filter(i => (i.product || '').trim()) : [];

  // ── Totals ──────────────────────────────────────────────────────────────────
  const taxableAmount  = Number(order.taxableAmount  || 0);
  const taxAmount      = Number(order.taxAmount      || 0);
  const grandTotal     = Number(order.grandTotal     || 0);
  const rawTotal       = taxableAmount + taxAmount;
  const roundOff       = Math.round(rawTotal) - rawTotal;
  const finalTotal     = Math.round(rawTotal);
  const amountReceived = Number(order.amountReceived || 0);
  const balanceDue     = Math.max(0, finalTotal - amountReceived);

  // ── Page height ──────────────────────────────────────────────────────────────
  const ROW_H    = 20;
  const hasGst   = taxAmount > 0;
  const hasPO    = !!order.isAgainstPO;
  const hasPaid  = amountReceived > 0;

  // HSN / SKU line shown under the description (only when the item has them)
  const buildMetaStr = (item) =>
    [
      String(item.hsn || '').trim() && `HSN: ${String(item.hsn).trim()}`,
      String(item.sku || '').trim() && `SKU: ${String(item.sku).trim()}`,
    ].filter(Boolean).join('   |   ').replace(/[^\x20-\x7E]/g, '');

  // Estimate row height for each item accounting for text wrapping
  const descColWEst = (PAGE_W - ML - MR) * 0.36 - 6;
  const calcRowH = (item, fB, fN) => {
    const productStr = String(item.product || '').replace(/[^\x20-\x7E]/g, '');
    const descStr    = String((item.description || '').trim()).replace(/[^\x20-\x7E]/g, '');
    const hasD = !!descStr;
    const metaStr = buildMetaStr(item);
    const hasM = !!metaStr;
    const countWrappedLines = (str, f, size, maxW) => {
      if (!str) return 0;
      const words = str.split(' ');
      let line = ''; let count = 1;
      for (const w of words) {
        const test = line ? line + ' ' + w : w;
        if (f.widthOfTextAtSize(test, size) > maxW && line) { count++; line = w; }
        else line = test;
      }
      return count;
    };
    // Match exact same constants as rendering: PROD_LH=10, DESC_LH=9, GAP=3, PAD=14
    const pLines = countWrappedLines(productStr, fB, 7.5, descColWEst) || 1;
    const dLines = hasD ? countWrappedLines(descStr, fN, 6.5, descColWEst) : 0;
    const mLines = hasM ? countWrappedLines(metaStr, fN, 6.5, descColWEst) : 0;
    const textBlockH = pLines * 10 + (hasD ? 3 + dLines * 9 : 0) + (hasM ? 3 + mLines * 9 : 0);
    return Math.max(ROW_H, textBlockH + 14);
  };

  const itemRowHeights = items.map(i => calcRowH(i, fontB, font));
  const totalItemsH   = itemRowHeights.reduce((s, h) => s + h, 0);
  const emptyRowsH    = Math.max(0, 8 - items.length) * ROW_H;

  const PAGE_H =
    30  +  // top margin
    1   +  // divider
    (hasPO ? 20 : 0) +  // PO row
    100 +  // customer block
    30  +  // table header
    totalItemsH + emptyRowsH +
    14 * 4 +  // subtotal, tax, roundoff, total rows
    30  +  // amount in words
    (hasGst ? 80 : 0) +  // GST breakdown table
    (hasPaid ? (18 + Math.max(1, (Array.isArray(order.paymentEntries) ? order.paymentEntries.filter(e=>Number(e.amount||0)>0).length : 1)) * 14 + 38) : 0) +  // payment summary
    40;    // footer + bottom margin

  const page = pdfDoc.addPage([PAGE_W, PAGE_H]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const safeStr = (s) => String(s ?? '').replace(/[^\x20-\x7E]/g, '');

  // NOTE: txt() NEVER passes maxWidth to pdf-lib — that causes double-wrap + overlap.
  // All wrapping is done manually via wrapText() before calling txt().
  const txt = (str, x, y, { size=8, bold=false, italic=false, color=BLACK } = {}) => {
    const f = bold ? fontB : italic ? fontO : font;
    const s = safeStr(str);
    if (!s) return;
    try {
      page.drawText(s, { x, y, size, font: f, color });
    } catch(e) {}
  };

  const hline = (y, x1=ML, x2=RIGHT, t=0.5, color=BLACK) =>
    page.drawLine({ start:{x:x1,y}, end:{x:x2,y}, thickness:t, color });

  const vline = (x, y1, y2, t=0.5, color=BLACK) =>
    page.drawLine({ start:{x,y:y1}, end:{x,y:y2}, thickness:t, color });

  const box = (x, y, w, h, { fill, border, t=0.5 } = {}) => {
    if (fill)   page.drawRectangle({ x, y, width:w, height:h, color:fill });
    if (border) page.drawRectangle({ x, y, width:w, height:h, borderColor:border, borderWidth:t });
  };

  const txtR = (str, rx, y, opts={}) => {
    const f = opts.bold ? fontB : font;
    const w = f.widthOfTextAtSize(safeStr(str), opts.size||8);
    txt(str, rx - w, y, opts);
  };

  const txtC = (str, x, w, y, opts={}) => {
    const f = opts.bold ? fontB : font;
    const tw = f.widthOfTextAtSize(safeStr(str), opts.size||8);
    txt(str, x + (w-tw)/2, y, opts);
  };
  // ── Word-wrap helper — NEVER use maxWidth in pdf-lib drawText, wrap manually ──
  const wrapText = (str, f, size, maxW) => {
    const safe = safeStr(str);
    if (!safe) return [];
    const words = safe.split(' ');
    const resultLines = [];
    let current = '';
    for (const word of words) {
      const test = current ? current + ' ' + word : word;
      if (f.widthOfTextAtSize(test, size) > maxW && current) {
        resultLines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) resultLines.push(current);
    return resultLines;
  };


  // ── Layout cursor ────────────────────────────────────────────────────────────
  let y = PAGE_H - 10;
  const borderTop = PAGE_H - 10;

  // OUTER BORDER drawn after all content (see end of function)

  // ── 1. ORDER NUMBER (left) + ORDER DATE (right) ───────────────────────────────
  // NOTE: "SALES ORDER" title bar removed — start directly with order number/date
  y = PAGE_H - 22;
  txt('Order No.', ML+5, y, { size: 6.5, color: GRAY });
  txtR('Order Date', RIGHT-5, y, { size: 6.5, color: GRAY });
  y -= 11;
  txt(order.orderNumber || '—', ML+5, y, { size: 9, bold: true });
  txtR(fmtDate(order.orderDate), RIGHT-5, y, { size: 9, bold: true });
  y -= 8;
  hline(y, ML, RIGHT, 1);

  // ── 2. PO REFERENCE (only if isAgainstPO) ────────────────────────────────────
  if (hasPO) {
    y -= 8;
    txt('PO No.', ML+5, y, { size: 6.5, color: GRAY });
    if (order.poDate) txtR('PO Date', RIGHT-5, y, { size: 6.5, color: GRAY });
    y -= 11;
    txt(order.poNumber || '—', ML+5, y, { size: 8, bold: true });
    if (order.poDate) txtR(fmtDate(order.poDate), RIGHT-5, y, { size: 8, bold: true });
    y -= 6;
    hline(y, ML, RIGHT, 0.5, LGRAY);
  }

  // ── 3. CUSTOMER BLOCK (full width — Order Details column removed) ─────────────
  const custTop = y;

  txt('Bill To', ML+5, y-8, { size: 6.5, color: GRAY });
  // NOTE: "Order Details" right column removed entirely

  const isBusiness = !!order.companyName;
  let cy = y - 20;

  const drawWrappedBlock = (str, x, startY, f, size, lh, opts={}) => {
    const lines = wrapText(str, f, size, CW - 14);
    let ly = startY;
    for (const line of lines) { txt(line, x, ly, { size, ...opts }); ly -= lh; }
    return ly;
  };

  if (isBusiness) {
    cy = drawWrappedBlock(order.companyName || '', ML+5, cy, fontB, 9, 12, { bold: true });
    if (order.companyAddress) { cy = drawWrappedBlock(order.companyAddress, ML+5, cy, font, 7.5, 11); }
    if (order.gstNo)          { txt('GSTIN: ' + order.gstNo, ML+5, cy, { size: 7.5 }); cy -= 11; }
  } else {
    cy = drawWrappedBlock(order.customerName || '', ML+5, cy, fontB, 9, 12, { bold: true });
    if (order.companyAddress) { cy = drawWrappedBlock(order.companyAddress, ML+5, cy, font, 7.5, 11, { color: GRAY }); }
    if (order.customerPhone)  { txt(order.customerPhone,  ML+5, cy, { size: 7.5 }); cy -= 11; }
    if (order.customerEmail)  { txt(order.customerEmail,  ML+5, cy, { size: 7.5 }); cy -= 11; }
    if (order.gstNo)          { txt('GSTIN: ' + order.gstNo, ML+5, cy, { size: 7.5 }); cy -= 11; }
  }

  const custBlockH = y - cy + 4;
  hline(custTop - custBlockH, ML, RIGHT, 1);
  y = custTop - custBlockH;

  // ── 4. LINE ITEMS TABLE ──────────────────────────────────────────────────────
  // Show Tax % column only when at least one item has tax > 0
  const anyTax = items.some(i => Number(i.taxPercent || i.gst) > 0);

  // Columns — when no tax, Tax % column is hidden and its width (9%) goes to Amount
  const C = anyTax ? {
    sl:   { x: ML,             w: CW*0.05 },
    desc: { x: ML+CW*0.05,     w: CW*0.36 },
    qty:  { x: ML+CW*0.41,     w: CW*0.09 },
    unit: { x: ML+CW*0.50,     w: CW*0.09 },
    rate: { x: ML+CW*0.59,     w: CW*0.13 },
    tax:  { x: ML+CW*0.72,     w: CW*0.09 },
    amt:  { x: ML+CW*0.81,     w: CW*0.19 },
  } : {
    sl:   { x: ML,             w: CW*0.05 },
    desc: { x: ML+CW*0.05,     w: CW*0.36 },
    qty:  { x: ML+CW*0.41,     w: CW*0.09 },
    unit: { x: ML+CW*0.50,     w: CW*0.09 },
    rate: { x: ML+CW*0.59,     w: CW*0.13 },
    tax:  null,
    amt:  { x: ML+CW*0.72,     w: CW*0.28 }, // expanded: absorbed tax column width
  };

  const TH = 22;
  box(ML, y-TH, CW, TH, { fill: FILL });
  hline(y, ML, RIGHT, 1);

  const headers = [
    [C.sl,   '#',                     'c'],
    [C.desc, 'Product / Description', 'l'],
    [C.qty,  'Qty',                   'r'],
    [C.unit, 'Unit',                  'c'],
    [C.rate, 'Rate',                  'r'],
    ...(anyTax ? [[C.tax, 'Tax %', 'c']] : []),
    [C.amt,  'Amount',                'r'],
  ];
  headers.forEach(([col, label, align]) => {
    const yy = y - 9;
    if (align === 'c') txtC(label, col.x, col.w, yy, { size: 7, bold: true });
    else if (align === 'r') txtR(label, col.x+col.w-3, yy, { size: 7, bold: true });
    else txt(label, col.x+3, yy, { size: 7, bold: true });
  });
  const dividerCols = [C.desc, C.qty, C.unit, C.rate, ...(anyTax ? [C.tax] : []), C.amt];
  dividerCols.forEach(col => vline(col.x, y, y-TH, 1));
  hline(y-TH, ML, RIGHT, 1);
  y -= TH;

  const bodyTop = y;
  let totalQty  = 0;

  const PROD_SIZE  = 7.5;
  const DESC_SIZE  = 6.5;
  const PROD_LH    = 10;   // line height for product (bold)
  const DESC_LH    = 9;    // line height for description
  const CELL_PAD_T = 7;    // top padding inside cell
  const CELL_PAD_B = 7;    // bottom padding inside cell
  const PROD_DESC_GAP = 3; // gap between product and description

  items.forEach((item, i) => {
    const qty    = Number(item.qty || item.quantity) || 0;
    const rate   = Number(item.price || item.rate)   || 0;
    const tax    = Number(item.taxPercent || item.gst) || 0;
    const base   = qty * rate;
    const amount = base + base * (tax / 100);
    totalQty += qty;

    const productStr = safeStr(item.product || '');
    const descStr    = safeStr((item.description || '').trim());
    const hasDesc    = !!descStr;
    const metaStr    = safeStr(buildMetaStr(item));
    const hasMeta    = !!metaStr;
    const descColW   = C.desc.w - 8;  // 4px margin each side

    // Wrap product (bold font) and description (regular font)
    const productWrapped = wrapText(productStr, fontB, PROD_SIZE, descColW);
    const descWrapped    = hasDesc ? wrapText(descStr, font, DESC_SIZE, descColW) : [];
    const metaWrapped    = hasMeta ? wrapText(metaStr, font, DESC_SIZE, descColW) : [];

    // Calculate exact row height needed
    const textBlockH =
      (productWrapped.length || 1) * PROD_LH +
      (hasDesc ? PROD_DESC_GAP + descWrapped.length * DESC_LH : 0) +
      (hasMeta ? PROD_DESC_GAP + metaWrapped.length * DESC_LH : 0);

    const rH = Math.max(ROW_H, textBlockH + CELL_PAD_T + CELL_PAD_B);

    // Draw product lines — top aligned
    let textY = y - CELL_PAD_T;
    for (const line of productWrapped) {
      txt(line, C.desc.x + 4, textY, { size: PROD_SIZE, bold: true });
      textY -= PROD_LH;
    }

    // Draw description lines below product
    if (hasDesc) {
      textY -= PROD_DESC_GAP;
      for (const line of descWrapped) {
        txt(line, C.desc.x + 4, textY, { size: DESC_SIZE, color: GRAY });
        textY -= DESC_LH;
      }
    }

    // HSN / SKU line below the description
    if (hasMeta) {
      textY -= PROD_DESC_GAP;
      for (const line of metaWrapped) {
        txt(line, C.desc.x + 4, textY, { size: DESC_SIZE, color: GRAY });
        textY -= DESC_LH;
      }
    }

    // Other columns vertically centred
    const midY = y - rH / 2 - 3;
    txtC(String(i+1),        C.sl.x,               C.sl.w,  midY, { size: 7.5, bold: true });
    txtR(String(qty),        C.qty.x  + C.qty.w-3, midY,          { size: 7.5 });
    txtC(item.unit || 'Nos', C.unit.x, C.unit.w,   midY,          { size: 7.5 });
    txtR(fmt2(rate),         C.rate.x + C.rate.w-3,midY,          { size: 7.5 });
    if (anyTax) txtC(tax ? tax+'%' : '—', C.tax.x, C.tax.w, midY, { size: 7.5 });
    txtR(fmt2(amount),       C.amt.x  + C.amt.w-4, midY,          { size: 7.5 });

    hline(y - rH, ML, RIGHT, 0.3, LGRAY);
    y -= rH;
  });

  // Pad to min 8 rows
  const emptyRows = Math.max(0, 8 - items.length);
  y -= emptyRows * ROW_H;

  dividerCols.forEach(col => vline(col.x, bodyTop, y, 1));
  hline(y, ML, RIGHT, 1);

  // ── 5. TOTALS ────────────────────────────────────────────────────────────────
  const totRows = [
    [anyTax ? 'Taxable Amount' : 'Amount', fmt2(taxableAmount), false],
    hasGst ? ['Tax Amount', fmt2(taxAmount), false] : null,
    roundOff !== 0 ? ['Round Off', (roundOff > 0 ? '+' : '-') + fmt2(Math.abs(roundOff)), false] : null,
    ['Grand Total', 'Rs. ' + fmt2(finalTotal), true],
  ].filter(Boolean);

  totRows.forEach(([label, value, bold]) => {
    txt(label, C.desc.x+3, y-10, { size: 8, bold, italic: !bold });
    txtR(value, C.amt.x+C.amt.w-4, y-10, { size: 8, bold });
    hline(y-14, ML, RIGHT, bold ? 1 : 0.4, bold ? BLACK : LGRAY);
    y -= 14;
  });

  // ── 6. AMOUNT IN WORDS ───────────────────────────────────────────────────────
  txt('Amount Chargeable (in words)', ML+4, y-9, { size: 6.5, color: GRAY });
  txtR('E. & O.E', RIGHT-4, y-9, { size: 6.5, italic: true });
  hline(y-12, ML, RIGHT, 0.4, LGRAY);
  txt('INR ' + toWords(finalTotal), ML+4, y-22, { size: 8, bold: true });
  hline(y-26, ML, RIGHT, 1);
  y -= 26;

  // ── 7. GST BREAKDOWN (only if there is tax) ──────────────────────────────────
  // if (hasGst) {
  //   const cgst = taxAmount / 2;
  //   const sgst = taxAmount / 2;
  //   const gstRateSet = [...new Set(items.map(i => Number(i.taxPercent || i.gst) || 0).filter(r => r > 0))];
  //   const gstRate = gstRateSet[0] || 18;

  //   const TAX_H = 68;
  //   const TC = [
  //     { label: 'HSN/SAC',          x: ML,         w: CW*0.12 },
  //     { label: 'Taxable\nValue',   x: ML+CW*0.12, w: CW*0.12 },
  //     { label: 'CGST\nRate  Amt',  x: ML+CW*0.24, w: CW*0.14 },
  //     { label: 'SGST\nRate  Amt',  x: ML+CW*0.38, w: CW*0.14 },
  //     { label: 'Total\nTax Amt',   x: ML+CW*0.52, w: CW*0.14 },
  //   ];

  //   box(ML, y-16, CW, 16, { fill: FILL });
  //   TC.forEach(col => {
  //     col.label.split('\n').forEach((line, i) =>
  //       txtC(line, col.x, col.w, y-6-i*7, { size: 6, bold: true })
  //     );
  //     vline(col.x, y, y-TAX_H, 0.4, LGRAY);
  //   });
  //   hline(y-16, ML, RIGHT, 0.5);

  //   const ty = y - 26;
  //   txtC('—',             TC[0].x, TC[0].w,              ty, { size: 7 });
  //   txtR(fmt2(taxableAmount), TC[1].x+TC[1].w-2,         ty, { size: 7 });
  //   txtC((gstRate/2)+'%', TC[2].x, TC[2].w*0.45,         ty, { size: 7 });
  //   txtR(fmt2(cgst),      TC[2].x+TC[2].w-2,             ty, { size: 7 });
  //   txtC((gstRate/2)+'%', TC[3].x, TC[3].w*0.45,         ty, { size: 7 });
  //   txtR(fmt2(sgst),      TC[3].x+TC[3].w-2,             ty, { size: 7 });
  //   txtR(fmt2(taxAmount), TC[4].x+TC[4].w-2,             ty, { size: 7 });
  //   hline(y-30, ML, RIGHT, 0.4, LGRAY);

  //   const ty2 = y - 40;
  //   txt('Total',          ML+3,                           ty2, { size: 7, bold: true });
  //   txtR(fmt2(taxableAmount), TC[1].x+TC[1].w-2,         ty2, { size: 7, bold: true });
  //   txtR(fmt2(cgst),      TC[2].x+TC[2].w-2,             ty2, { size: 7, bold: true });
  //   txtR(fmt2(sgst),      TC[3].x+TC[3].w-2,             ty2, { size: 7, bold: true });
  //   txtR(fmt2(taxAmount), TC[4].x+TC[4].w-2,             ty2, { size: 7, bold: true });
  //   hline(y-44, ML, RIGHT, 0.5);

  //   txt('Tax Amount (in words): INR ' + toWords(taxAmount), ML+3, y-54, { size: 6.5, bold: true });
  //   hline(y-TAX_H, ML, RIGHT, 1);
  //   y -= TAX_H;
  // }

  // ── 8. PAYMENT SUMMARY (only if payments recorded) ───────────────────────────
  const payEntries = Array.isArray(order.paymentEntries) ? order.paymentEntries.filter(e => Number(e.amount||0) > 0) : [];

  if (hasPaid || payEntries.length > 0) {
    const isFullyPaid = balanceDue <= 0;

    // Column positions for the payment table
    const PC = {
      no:     { x: ML,          w: CW*0.06 },
      date:   { x: ML+CW*0.06,  w: CW*0.22 },
      mode:   { x: ML+CW*0.28,  w: CW*0.22 },
      amt:    { x: ML+CW*0.50,  w: CW*0.50 },
    };

    const PAY_HEADER_H = 18;
    const PAY_ROW_H    = 14;
    const PAY_TOTAL_H  = 16;
    const PAY_DUE_H    = isFullyPaid ? 0 : 16;
    const PAY_H = PAY_HEADER_H + (payEntries.length * PAY_ROW_H) + PAY_TOTAL_H + PAY_DUE_H + 6;

    // Header background
    box(ML, y - PAY_HEADER_H, CW, PAY_HEADER_H, { fill: FILL });
    hline(y, ML, RIGHT, 1);

    // Header labels
    txtC('#',              PC.no.x,   PC.no.w,   y-6,  { size: 6.5, bold: true });
    txt('Date',            PC.date.x+3, y-6,            { size: 6.5, bold: true });
    txt('Mode',            PC.mode.x+3, y-6,            { size: 6.5, bold: true });
    txtR('Amount',         PC.amt.x+PC.amt.w-4, y-6,   { size: 6.5, bold: true });

    // Column dividers for header
    [PC.date, PC.mode, PC.amt].forEach(col => vline(col.x, y, y-PAY_HEADER_H, 0.5, LGRAY));
    hline(y-PAY_HEADER_H, ML, RIGHT, 0.8);
    y -= PAY_HEADER_H;

    // Payment rows
    payEntries.forEach((entry, idx) => {
      const rowY = y - PAY_ROW_H/2 - 3;
      const entryAmt = Number(entry.amount || 0);
      const entryDate = fmtDate(entry.receiptDate || entry.date || '');
      const modeRaw = String(entry.mode || 'cash');
      const modeLabel = modeRaw === 'bank_transfer' ? 'Bank Transfer'
        : modeRaw.charAt(0).toUpperCase() + modeRaw.slice(1);

      txtC(String(idx+1),  PC.no.x,   PC.no.w,   rowY, { size: 7 });
      txt(entryDate,        PC.date.x+3, rowY,          { size: 7 });
      txt(modeLabel,        PC.mode.x+3, rowY,          { size: 7 });
      txtR('Rs. '+fmt2(entryAmt), PC.amt.x+PC.amt.w-4, rowY, { size: 7, bold: true, color: rgb(0.1,0.55,0.1) });

      [PC.date, PC.mode, PC.amt].forEach(col => vline(col.x, y, y-PAY_ROW_H, 0.4, LGRAY));
      hline(y-PAY_ROW_H, ML, RIGHT, 0.3, LGRAY);
      y -= PAY_ROW_H;
    });

    // Total received row
    box(ML, y-PAY_TOTAL_H, CW, PAY_TOTAL_H, { fill: rgb(0.93,0.98,0.93) });
    txt('Total Amount Received', ML+5, y-PAY_TOTAL_H/2-3, { size: 7.5, bold: true });
    txtR('Rs. '+fmt2(amountReceived), RIGHT-5, y-PAY_TOTAL_H/2-3, { size: 7.5, bold: true, color: rgb(0.1,0.55,0.1) });
    hline(y-PAY_TOTAL_H, ML, RIGHT, 0.8);
    y -= PAY_TOTAL_H;

    // Balance due row — only if NOT fully paid
    if (!isFullyPaid) {
      box(ML, y-PAY_DUE_H, CW, PAY_DUE_H, { fill: rgb(1,0.96,0.96) });
      txt('Total Amount Due', ML+5, y-PAY_DUE_H/2-3, { size: 7.5, bold: true });
      txtR('Rs. '+fmt2(balanceDue), RIGHT-5, y-PAY_DUE_H/2-3, { size: 7.5, bold: true, color: rgb(0.8,0.1,0.1) });
      hline(y-PAY_DUE_H, ML, RIGHT, 1);
      y -= PAY_DUE_H;
    } else {
      hline(y, ML, RIGHT, 1);
    }

    y -= 6;
  }

  // ── 9. NOTES (if any) ───────────────────────────────────────────────────────
  if (order.notes) {
    txt('Notes', ML+5, y-9, { size: 6.5, color: GRAY });
    const noteLines = wrapText(order.notes, font, 7.5, CW - 10);
    let ny = y - 20;
    for (const nl of noteLines) { txt(nl, ML+5, ny, { size: 7.5 }); ny -= 10; }
    hline(y-34, ML, RIGHT, 1);
    y -= 34;
  }

  // ── 10. FOOTER ───────────────────────────────────────────────────────────────
  txtC('This is a Computer Generated Document', ML, CW, y-10, { size: 6.5, color: GRAY, italic: true });
  const footerBottom = y - 20;

  // OUTER BORDER — drawn now using actual content height (no blank space)
  box(ML, footerBottom, CW, borderTop - footerBottom, { border: BLACK, t: 1 });

  return pdfDoc.save();
}

export function downloadSalesOrderPDF(pdfBytes, orderNumber) {
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `SalesOrder-${(orderNumber || 'draft').replace(/\//g, '-')}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}